import React, { useContext, useMemo } from 'react';

import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
} from '@components/Dialog';
import { CustomsInstructionContext } from '@components/utilities/Bookings/helpers2';
import { VerticalSpace } from '@components/VerticalSpace';
import { instructionStrings } from '@data/instruction';
import { useIsAdmin } from '@hooks/useIsAdmin';
import { CustomsInstructionForm } from '@models/next/CustomsInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { SafeParseReturnType } from 'zod';

const strings = createStrings({
  ja: {
    title: '送信の確認',
    infoIsNotEnough: `必須項目の入力が不足しているため、「${instructionStrings.customsFiles}のアップロードと${instructionStrings.seaInsurance}」を完了できませんでした。`,
    confirmErrorsPrompt: '次の箇所を再度ご確認ください：',
    thereIsEmptyField: (isAdmin: boolean) =>
      `入力欄が空欄、もしくは選択肢が選ばれていないようです。再度ご確認のうえ、${
        isAdmin ? '保存' : '送信'
      }ボタンを押してしてください。`,
    rcepAlert: (isAdmin: boolean) => (
      <>
        <b>
          中国原産地証明書はアップロードされていません。このまま
          {isAdmin ? formStrings.confirm : formStrings.submit}
          しますか？
        </b>
        <br />
        {!isAdmin && (
          <>
            送信すると、書類の差替え・追加はできなくなります。
            <br />
            <br />
            通関書類・商品情報の差替え・訂正・追加をしたい場合、
            <br />
            左下の「...」アイコンをクリックして、D2Dカスタマーセンターへご連絡ください。
          </>
        )}
      </>
    ),
  },
});

const useStyles = makeMuiStyles((_theme, common) => ({
  contentContainer: {
    padding: '1rem 1rem 2rem 1rem',
  },
  buttonContainerBetween: {
    ...common.flexInline,
    ...common.justifyBetween,
    padding: '1.5rem 2rem',
  },
  buttonContainerCenter: {
    ...common.flexInline,
    ...common.justifyCenter,
    padding: '1.5rem 2rem',
  },
}));

export interface CustomsInstructionAlertDialogProps {
  open: boolean;
  parsed?: SafeParseReturnType<
    (typeof CustomsInstructionForm)['_input'],
    (typeof CustomsInstructionForm)['_output']
  >;
  onClose: () => void;
  showRcepAlert: boolean;
}

export const CustomsInstructionAlertDialog: React.FC<CustomsInstructionAlertDialogProps> = ({
  open,
  parsed,
  onClose,
  showRcepAlert,
}) => {
  const isAdmin = useIsAdmin();

  const { form } = useContext(CustomsInstructionContext);

  const classes = { ...useCommonStyles(), ...useStyles() };

  const errorLabels = useMemo(() => {
    if (parsed === undefined || parsed?.success) {
      return [];
    } else {
      const e = parsed.error.format();
      return [
        e.custom_files && instructionStrings.customsFiles,
        e.commodities && instructionStrings.commodities,
        e.insurance && instructionStrings.seaInsuranceUsage,
        (e.has_special_note || e.special_note) && instructionStrings.specialNoteDetails,
      ].filter(Boolean);
    }
  }, [parsed]);

  return (
    <ActionableDialog size="lg" open={open} onClose={onClose}>
      <ActionableDialogTitle>{strings.title}</ActionableDialogTitle>

      <ActionableDialogContent>
        <div className={classes.contentContainer}>
          {showRcepAlert && strings.rcepAlert(isAdmin)}

          {errorLabels.length > 0 && (
            <>
              <div className={clsx(classes.textError, classes.fontWeight700)}>
                {strings.infoIsNotEnough}
              </div>
              <VerticalSpace height={20} />
              <div>
                {strings.confirmErrorsPrompt}
                <span className={clsx(classes.textError, classes.fontWeight700)}>
                  {errorLabels.map((label) => `「${label}」`).join(formStrings.comma)}
                </span>
              </div>
              <VerticalSpace height={20} />
              <div>{strings.thereIsEmptyField(isAdmin)}</div>
              <VerticalSpace height={80} />
            </>
          )}
        </div>
      </ActionableDialogContent>
      <ActionableDialogActions justifyContent="center">
        {showRcepAlert && (
          <>
            <Button
              color="grey"
              size="small"
              text={formStrings.prev}
              onClick={onClose}
              type="button"
              style={{ width: 150 }}
            />
            <Button
              color="primary"
              size="small"
              text={isAdmin ? formStrings.confirm : formStrings.submit}
              type="button"
              onClick={() => {
                form.root.fields.action.setOutput('submit');
                form.element?.dispatchEvent(
                  new Event('submit', { cancelable: true, bubbles: true }),
                );
                onClose();
              }}
              style={{ width: 150 }}
            />
          </>
        )}

        {errorLabels.length > 0 && (
          <>
            <Button
              color="primary"
              size="small"
              type="button"
              text={formStrings.prev}
              onClick={onClose}
              style={{ width: 150 }}
            />
          </>
        )}
      </ActionableDialogActions>
    </ActionableDialog>
  );
};
